import React, { MouseEventHandler, ReactNode } from "react";
import styled from "@emotion/styled";
import { Box, Theme, Typography, useMediaQuery, useTheme } from "@mui/material";
import { css } from "@emotion/react";
import { useNavigate } from "react-router";
import { useAppLayout } from "@providers/AppLayoutProvider";

const Container = styled(Box)<{
  selected: boolean;
}>`
  width: 100%;
  display: flex;
  text-decoration: none;
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};

  ${({
    selected,
    theme: {
      palette: { sidebar },
    },
  }) =>
    selected
      ? css`
          color: ${sidebar.sidebarColorOnDark};
          background-color: ${sidebar.active};
        `
      : css`
          color: ${sidebar.textOnSidebar};
          background-color: ${sidebar.inactive};

          &:hover {
            color: ${sidebar.sidebarColorOnDark};
            background-color: ${sidebar.hover};
          }
        `}
`;

export const ItemContainer = styled(Box)<{
  selected: boolean;
}>`
  text-decoration: none;
  display: flex;
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  justify-items: end;
  align-items: center;
  cursor: pointer;
  color: inherit;
`;

export const MenuItemText = styled(Typography)`
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-weight: 500;
`;

export interface MenuItemProps {
  name: string;
  before?: ReactNode;
  after?: ReactNode;
  selected?: boolean;
  children?: ReactNode;
  to?: string;
  state?: Record<string, unknown>;
  onClick?: MouseEventHandler;
}

export const MenuItem = ({
  name,
  selected = false,
  before,
  after,
  to,
  state,
  onClick,
}: MenuItemProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { sidebarCollapsed: collapsed, toggleSidebarCollapsed } =
    useAppLayout();
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  // check if we have a valid url (with protocol) then replace location.
  // if not - then we have our local router path and should use navigate()
  const handleClick = () => {
    if (!to) return;

    try {
      const url = new URL(to);
      window.location.assign(url);
      if (isXs) {
        toggleSidebarCollapsed();
      }
    } catch (err) {
      navigate(to, {
        state,
      });
      if (isXs) {
        toggleSidebarCollapsed();
      }
    }
  };

  return (
    <Box width="100%">
      <Container onClick={onClick || handleClick} selected={selected}>
        <ItemContainer width="100%" selected={selected} className="menuItem">
          {before && (
            <Box display="flex" mr={collapsed ? 0 : 4}>
              {before}
            </Box>
          )}
          {!collapsed && <MenuItemText variant="body3">{name}</MenuItemText>}
          {after && after}
        </ItemContainer>
      </Container>
    </Box>
  );
};
